import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import useAuth from '../hooks/useAuth';

const ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'cashier', label: 'Cashier' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
];

const DEFAULT_FORM = {
  username: '',
  email: '',
  displayName: '',
  role: 'cashier',
  status: 'active',
  sendInvite: true,
};

const StatusBadge = ({ status }) => {
  const classes =
    status === 'active'
      ? 'bg-emerald-400/20 text-emerald-200 border-emerald-400/40'
      : 'bg-rose-400/10 text-rose-200 border-rose-400/40';
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${classes}`}>
      {status}
    </span>
  );
};

const RoleBadge = ({ role }) => {
  const colors = {
    admin: 'bg-violet-500/20 text-violet-200 border-violet-400/40',
    manager: 'bg-sky-500/20 text-sky-200 border-sky-400/40',
    cashier: 'bg-slate-500/20 text-slate-200 border-slate-400/40',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${colors[role] || colors.cashier}`}>
      {role}
    </span>
  );
};

const StatCard = ({ label, value, tone = 'slate' }) => {
  const palette = {
    slate: 'border-slate-800 bg-slate-950/50 text-slate-100',
    emerald: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100',
    rose: 'border-rose-500/40 bg-rose-500/10 text-rose-100',
  };
  return (
    <div className={`rounded-2xl border px-4 py-3 ${palette[tone] || palette.slate}`}>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
    </div>
  );
};

export default function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ role: '', status: '', search: '' });
  const [searchDraft, setSearchDraft] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [resettingId, setResettingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [showBulkInvite, setShowBulkInvite] = useState(false);
  const [bulkInviteText, setBulkInviteText] = useState('');
  const [bulkInviteRole, setBulkInviteRole] = useState('cashier');
  const [bulkInviting, setBulkInviting] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/users', {
        params: {
          role: filters.role || undefined,
          status: filters.status || undefined,
          search: filters.search || undefined,
        },
      });
      setUsers(res.data.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to load team.');
    } finally {
      setLoading(false);
    }
  }, [filters.role, filters.status, filters.search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchDraft.trim() }));
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        username: form.username.trim(),
        email: form.email.trim() || undefined,
        displayName: form.displayName.trim() || undefined,
        role: form.role,
        status: form.status,
        sendInvite: form.sendInvite,
      };
      const res = await api.post('/users', payload);
      toast.success('Team member added.');
      if (res.data?.temporaryPassword) {
        toast((t) => (
          <div>
            <p className="font-semibold">Temporary password</p>
            <p className="font-mono text-sm">{res.data.temporaryPassword}</p>
          </div>
        ), { duration: 8000 });
      }
      setForm(DEFAULT_FORM);
      setShowModal(false);
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create user.');
    } finally {
      setSaving(false);
    }
  };

  const parseBulkEmails = () =>
    Array.from(
      new Set(
        bulkInviteText
          .split(/[\s,;]+/)
          .map((email) => email.trim().toLowerCase())
          .filter((email) => /\S+@\S+\.\S+/.test(email))
      )
    );

  const handleBulkInviteSubmit = async (event) => {
    event.preventDefault();
    const emails = parseBulkEmails();
    if (emails.length === 0) {
      toast.error('Add at least one valid email address.');
      return;
    }
    setBulkInviting(true);
    const results = [];
    for (let i = 0; i < emails.length; i += 1) {
      const email = emails[i];
      const baseUsername = email.split('@')[0].replace(/[^a-z0-9]/gi, '') || 'user';
      const username = `${baseUsername}${Date.now().toString().slice(-4)}${i}`;
      try {
        await api.post('/users', {
          username,
          email,
          role: bulkInviteRole,
          status: 'active',
          sendInvite: true,
        });
        results.push({ email, status: 'success' });
      } catch (err) {
        results.push({
          email,
          status: 'error',
          message: err.response?.data?.error || err.message,
        });
      }
    }
    setBulkResults(results);
    setBulkInviting(false);
    await loadUsers();
  };

  const handleStatusChange = async (user, nextStatus) => {
    setUpdatingId(user.id);
    try {
      await api.put(`/users/${user.id}`, { status: nextStatus });
      toast.success(`User marked as ${nextStatus}.`);
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to update user.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleResetPassword = async (user) => {
    setResettingId(user.id);
    try {
      await api.post(`/users/${user.id}/reset-password`);
      toast.success(`Reset link sent to ${user.email || 'the user'}.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to send reset link.');
    } finally {
      setResettingId(null);
    }
  };

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => a.username.localeCompare(b.username));
  }, [users]);

  const stats = useMemo(() => {
    const result = {
      total: users.length,
      active: users.filter((user) => user.status === 'active').length,
      disabled: users.filter((user) => user.status !== 'active').length,
      roles: users.reduce(
        (acc, user) => {
          acc[user.role] = (acc[user.role] || 0) + 1;
          return acc;
        },
        { admin: 0, manager: 0, cashier: 0 }
      ),
    };
    return result;
  }, [users]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 rounded-3xl border border-slate-800 bg-slate-900/60 px-6 py-6 text-white lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-slate-500">Team</p>
          <h1 className="text-3xl font-semibold text-white">User management</h1>
          <p className="mt-2 text-sm text-slate-400">
            Manage staff accounts, assign roles, and trigger password resets without leaving the POS shell.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowBulkInvite(true)}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-700 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500"
          >
            Bulk invite
          </button>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="inline-flex items-center justify-center rounded-2xl bg-emerald-500 px-5 py-3 text-base font-semibold text-slate-900 transition hover:bg-emerald-400"
          >
            Add teammate
          </button>
        </div>
      </header>

      <section className="rounded-3xl border border-slate-800 bg-slate-900/50 p-6 text-white">
        <div className="grid gap-3 md:grid-cols-3">
          <StatCard label="Team members" value={stats.total} />
          <StatCard label="Active" value={stats.active} tone="emerald" />
          <StatCard label="Disabled" value={stats.disabled} tone="rose" />
        </div>

        <form onSubmit={handleSearchSubmit} className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Search</label>
            <input
              type="text"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder="Search username, email, or display name"
              className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-950 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Role</label>
            <select
              value={filters.role}
              onChange={(e) => setFilters((prev) => ({ ...prev, role: e.target.value }))}
              className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-950 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-950 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="rounded-2xl bg-slate-800 px-5 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-slate-700"
          >
            Apply
          </button>
        </form>

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-800">
          <table className="min-w-full divide-y divide-slate-800 text-sm">
            <thead className="bg-slate-900/80 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Operator</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Last login</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-950/40 text-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                    Loading team...
                  </td>
                </tr>
              ) : sortedUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                    No users found for the selected filters.
                  </td>
                </tr>
              ) : (
                sortedUsers.map((member) => (
                  <tr key={member.id}>
                    <td className="px-4 py-4">
                      <p className="font-semibold text-white">{member.displayName || member.username}</p>
                      <p className="text-xs text-slate-400">{member.email || '—'}</p>
                    </td>
                    <td className="px-4 py-4">
                      <RoleBadge role={member.role} />
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge status={member.status} />
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-300">
                      {member.lastLoginAt ? new Date(member.lastLoginAt).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          disabled={!member.email || resettingId === member.id}
                          onClick={() => handleResetPassword(member)}
                          className="rounded-xl border border-slate-700 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {resettingId === member.id ? 'Sending...' : 'Reset'}
                        </button>
                        {member.status === 'active' ? (
                          <button
                            type="button"
                            disabled={member.id === currentUser?.id || updatingId === member.id}
                            onClick={() => handleStatusChange(member, 'disabled')}
                            className="rounded-xl border border-rose-500/60 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-rose-200 transition hover:bg-rose-500/10 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {updatingId === member.id ? 'Updating...' : 'Disable'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={updatingId === member.id}
                            onClick={() => handleStatusChange(member, 'active')}
                            className="rounded-xl border border-emerald-500/60 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-emerald-200 transition hover:bg-emerald-500/10 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {updatingId === member.id ? 'Updating...' : 'Activate'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="w-full max-w-lg rounded-3xl border border-slate-800 bg-slate-950 p-6 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-semibold">Add teammate</h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-2xl text-slate-500 hover:text-white"
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <p className="mt-1 text-sm text-slate-400">Invite a new cashier, manager, or admin to the POS.</p>

            <form onSubmit={handleCreateUser} className="mt-6 space-y-4">
              <p className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-3 text-xs text-slate-400">
                Invites generate a secure 12-character password automatically. Recipients must change it on first
                login via the reset link included in their email.
              </p>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Username</label>
                <input
                  value={form.username}
                  onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                  required
                  className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Display name</label>
                <input
                  value={form.displayName}
                  onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
                  className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  placeholder="Used for invites and reset links"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Role</label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
                    className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  >
                    {ROLE_OPTIONS.filter((r) => r.value).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                    className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  >
                    {STATUS_OPTIONS.filter((r) => r.value).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-3 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={form.sendInvite}
                  onChange={(e) => setForm((prev) => ({ ...prev, sendInvite: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
                />
                Send onboarding email with a password reset link
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-2xl border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 hover:border-slate-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-2xl bg-emerald-500 px-6 py-2 text-sm font-semibold text-slate-900 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {saving ? 'Saving...' : 'Create user'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkInvite && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/70 px-4"
          onClick={() => setShowBulkInvite(false)}
        >
          <div
            className="w-full max-w-2xl rounded-3xl border border-slate-800 bg-slate-950 p-6 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-semibold">Bulk invite</h3>
              <button
                type="button"
                onClick={() => setShowBulkInvite(false)}
                className="text-2xl text-slate-500 hover:text-white"
                aria-label="Close bulk invite"
              >
                &times;
              </button>
            </div>
            <p className="mt-1 text-sm text-slate-400">
              Paste one or more email addresses (comma, space, or newline separated). Each address will get an invite.
            </p>

            <form onSubmit={handleBulkInviteSubmit} className="mt-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Emails</label>
                <textarea
                  value={bulkInviteText}
                  onChange={(e) => setBulkInviteText(e.target.value)}
                  rows={6}
                  className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  placeholder="user1@example.com&#10;user2@example.com"
                  required
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Default role</label>
                <select
                  value={bulkInviteRole}
                  onChange={(e) => setBulkInviteRole(e.target.value)}
                  className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                >
                  {ROLE_OPTIONS.filter((option) => option.value).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {bulkResults.length > 0 && (
                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-sm">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Latest results</h4>
                  <ul className="mt-2 space-y-1 text-slate-300">
                    {bulkResults.map((result) => (
                      <li key={`${result.email}-${result.status}`} className="flex items-center justify-between text-xs">
                        <span>{result.email}</span>
                        <span
                          className={
                            result.status === 'success'
                              ? 'text-emerald-300 font-semibold'
                              : 'text-rose-300 font-semibold'
                          }
                        >
                          {result.status === 'success' ? 'Invited' : result.message || 'Failed'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowBulkInvite(false)}
                  className="rounded-2xl border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 hover:border-slate-500"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={bulkInviting}
                  className="rounded-2xl bg-emerald-500 px-6 py-2 text-sm font-semibold text-slate-900 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {bulkInviting ? 'Inviting...' : 'Send invites'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
